/** @format */

import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
    Box,
    Typography,
    Table,
    TableBody,
    TableHead,
    TableRow,
    TextField,
    InputAdornment,
    MenuItem,
    CircularProgress,
    IconButton,
    Tooltip,
} from "@mui/material";
import {
    Search,
    Refresh,
    SyncAlt,
    OpenInNew,
    FilterList,
} from "@mui/icons-material";

import { AppDispatch } from "../../redux/store/store";
import {
    fetchInvoices,
    fetchCustomers,
    syncInvoices,
} from "../../redux/actions/collectAction";
import {
    selectActiveBusinessId,
    selectInvoices,
    selectIsLoadingInvoices,
    selectInvoicesError,
    selectInvoicesCursor,
    selectCustomers,
    selectIsSyncing,
} from "../../redux/selectors/collectSelector";
import {
    ICollectInvoice,
    CollectInvoiceStatus,
} from "../../utility/types/collectTypes";
import { colors } from "../../utility/types/constant";
import { useToast } from "../../providers/ToastProvider";
import {
    CollectPageWrapper,
    CollectSectionCard,
    CollectStatusChip,
    CollectTableContainer,
    CollectTableHeadCell,
    CollectTableBodyCell,
    CollectEmptyState,
    CollectEmptyTitle,
    CollectEmptyHint,
    CollectPrimaryButton,
    CollectGhostButton,
    CollectInfoBanner,
    FlexBetween,
} from "./styles";

// ─────────────────────────────────────────────────────────────────────────
// CollectInvoices — full invoice list with filters, search and cursor
// pagination. Mirrors the QuickBooks "All Invoices" page.
// ─────────────────────────────────────────────────────────────────────────

const STATUS_OPTIONS: Array<{ value: "all" | CollectInvoiceStatus; label: string }> = [
    { value: "all", label: "All statuses" },
    { value: "open", label: "Open" },
    { value: "overdue", label: "Overdue" },
    { value: "partially_paid", label: "Partially paid" },
    { value: "paid", label: "Paid" },
    { value: "draft", label: "Draft" },
    { value: "void", label: "Void" },
];

function formatMoney(value: number, currency = "USD"): string {
    try {
        return new Intl.NumberFormat("en-US", {
            style: "currency",
            currency,
            minimumFractionDigits: 2,
        }).format(value || 0);
    } catch {
        return `$${(value || 0).toFixed(2)}`;
    }
}

function formatDate(value?: string): string {
    if (!value) return "—";
    try {
        return new Date(value).toLocaleDateString("en-US", {
            month: "short",
            day: "numeric",
            year: "numeric",
            // QBO date-only ISO strings are at UTC midnight. Force UTC so the
            // displayed day matches the date the user picked in QuickBooks.
            timeZone: "UTC",
        });
    } catch {
        return value;
    }
}

function getDisplayName(inv: ICollectInvoice): string {
    return (
        (inv as any).customerDisplayName ||
        inv.customer?.displayName ||
        "Customer"
    );
}

const CollectInvoices: React.FC = () => {
    const dispatch = useDispatch<AppDispatch>();
    const navigate = useNavigate();
    const toast = useToast();
    const [searchParams, setSearchParams] = useSearchParams();

    const businessId = useSelector(selectActiveBusinessId);
    const invoices = useSelector(selectInvoices);
    const isLoading = useSelector(selectIsLoadingInvoices);
    const error = useSelector(selectInvoicesError);
    const nextCursor = useSelector(selectInvoicesCursor);
    const customers = useSelector(selectCustomers);
    const isSyncing = useSelector(selectIsSyncing);

    // Local filter state (initialized from URL params so the page is
    // shareable + survives back/forward).
    const [statusFilter, setStatusFilter] = useState<string>(
        searchParams.get("status") || "all",
    );
    const [customerFilter, setCustomerFilter] = useState<string>(
        searchParams.get("customerId") || "all",
    );
    const [query, setQuery] = useState<string>(searchParams.get("q") || "");

    // Reset filter state when navigating away/back if URL params change.
    useEffect(() => {
        setStatusFilter(searchParams.get("status") || "all");
        setCustomerFilter(searchParams.get("customerId") || "all");
        setQuery(searchParams.get("q") || "");
    }, [searchParams]);

    const queryPayload = useMemo(() => {
        const p: any = { limit: 25 };
        if (statusFilter !== "all") p.status = statusFilter;
        if (customerFilter !== "all") p.customerId = customerFilter;
        if (query.trim()) p.q = query.trim();
        return p;
    }, [statusFilter, customerFilter, query]);

    // Fetch invoices whenever the filter payload changes, and load the
    // small customer dropdown once we know the active business.
    useEffect(() => {
        if (!businessId) return;
        dispatch(fetchInvoices(queryPayload));
    }, [dispatch, businessId, queryPayload]);

    useEffect(() => {
        if (!businessId) return;
        if (customers.length === 0) {
            dispatch(fetchCustomers({ limit: 200 }));
        }
    }, [dispatch, businessId, customers.length]);

    const applyFilters = (next: {
        status?: string;
        customerId?: string;
        q?: string;
    }) => {
        const params: Record<string, string> = {};
        const status = next.status ?? statusFilter;
        const cust = next.customerId ?? customerFilter;
        const q = next.q ?? query;
        if (status && status !== "all") params.status = status;
        if (cust && cust !== "all") params.customerId = cust;
        if (q && q.trim()) params.q = q.trim();
        setSearchParams(params, { replace: true });
    };

    const onRefresh = () => {
        if (!businessId) return;
        dispatch(fetchInvoices(queryPayload));
    };

    const onSync = async () => {
        if (!businessId) return;
        const result: any = await dispatch(syncInvoices(undefined));
        if (result?.meta?.requestStatus === "fulfilled") {
            toast.success("Invoices synced from QuickBooks.");
        } else {
            toast.error("Couldn't sync invoices. Try again.");
        }
        dispatch(fetchInvoices(queryPayload));
    };

    const onRow = (inv: ICollectInvoice) => {
        navigate(`/collect/invoices/${inv._id}`);
    };

    return (
        <CollectPageWrapper>
            <Box
                sx={{
                    maxWidth: "1200px",
                    margin: "0 auto",
                    padding: { xs: "20px 12px", md: "32px 24px" },
                }}
            >
                <FlexBetween style={{ marginBottom: 8 }}>
                    <Box>
                        <Typography
                            sx={{
                                fontFamily: "'Montserrat', sans-serif",
                                fontWeight: 800,
                                fontSize: "clamp(1.4rem, 2.4vw, 1.8rem)",
                                color: colors.primary,
                            }}
                        >
                            Invoices
                        </Typography>
                        <Typography
                            sx={{
                                fontFamily: "'Montserrat', sans-serif",
                                color: "#6b7280",
                                fontSize: "0.9rem",
                                mt: 0.5,
                            }}
                        >
                            Every invoice synced from QuickBooks. Filter to find
                            anything overdue, partially paid, or due soon.
                        </Typography>
                    </Box>
                    <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                        <CollectGhostButton
                            startIcon={<Refresh fontSize="small" />}
                            onClick={onRefresh}
                            disabled={isLoading}
                        >
                            Refresh
                        </CollectGhostButton>
                        <CollectPrimaryButton
                            startIcon={
                                isSyncing ? (
                                    <CircularProgress size={16} sx={{ color: "#fff" }} />
                                ) : (
                                    <SyncAlt fontSize="small" />
                                )
                            }
                            onClick={onSync}
                            disabled={isSyncing || !businessId}
                        >
                            {isSyncing ? "Syncing…" : "Sync from QuickBooks"}
                        </CollectPrimaryButton>
                    </Box>
                </FlexBetween>

                {error && (
                    <CollectInfoBanner tone="warn">
                        <span>{error}</span>
                    </CollectInfoBanner>
                )}

                <CollectSectionCard>
                    {/* Filter row */}
                    <Box
                        sx={{
                            display: "grid",
                            gridTemplateColumns: {
                                xs: "1fr",
                                sm: "1fr 1fr",
                                md: "1.6fr 1fr 1fr",
                            },
                            gap: 2,
                            mb: 2.5,
                        }}
                    >
                        <TextField
                            size="small"
                            placeholder="Search by invoice # or memo…"
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === "Enter") applyFilters({ q: query });
                            }}
                            onBlur={() => applyFilters({ q: query })}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Search fontSize="small" />
                                    </InputAdornment>
                                ),
                                sx: { fontFamily: "'Montserrat', sans-serif" },
                            }}
                        />
                        <TextField
                            size="small"
                            select
                            label="Status"
                            value={statusFilter}
                            onChange={(e) => {
                                setStatusFilter(e.target.value);
                                applyFilters({ status: e.target.value });
                            }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <FilterList fontSize="small" />
                                    </InputAdornment>
                                ),
                                sx: { fontFamily: "'Montserrat', sans-serif" },
                            }}
                        >
                            {STATUS_OPTIONS.map((opt) => (
                                <MenuItem key={opt.value} value={opt.value}>
                                    {opt.label}
                                </MenuItem>
                            ))}
                        </TextField>
                        <TextField
                            size="small"
                            select
                            label="Customer"
                            value={customerFilter}
                            onChange={(e) => {
                                setCustomerFilter(e.target.value);
                                applyFilters({ customerId: e.target.value });
                            }}
                            InputProps={{
                                sx: { fontFamily: "'Montserrat', sans-serif" },
                            }}
                        >
                            <MenuItem value="all">All customers</MenuItem>
                            {customers.map((c) => (
                                <MenuItem key={c._id} value={c._id}>
                                    {c.displayName}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Box>

                    {/* Loading state */}
                    {isLoading && invoices.length === 0 ? (
                        <Box
                            sx={{
                                display: "flex",
                                justifyContent: "center",
                                py: 6,
                            }}
                        >
                            <CircularProgress size={28} sx={{ color: colors.mainSecondary }} />
                        </Box>
                    ) : invoices.length === 0 ? (
                        <CollectEmptyState>
                            <CollectEmptyTitle>No invoices found</CollectEmptyTitle>
                            <CollectEmptyHint>
                                Try clearing your filters or running a fresh sync from
                                QuickBooks to pull the latest invoices.
                            </CollectEmptyHint>
                        </CollectEmptyState>
                    ) : (
                        <CollectTableContainer>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <CollectTableHeadCell>Invoice #</CollectTableHeadCell>
                                        <CollectTableHeadCell>Customer</CollectTableHeadCell>
                                        <CollectTableHeadCell>Issued</CollectTableHeadCell>
                                        <CollectTableHeadCell>Due</CollectTableHeadCell>
                                        <CollectTableHeadCell align="right">
                                            Total
                                        </CollectTableHeadCell>
                                        <CollectTableHeadCell align="right">
                                            Open balance
                                        </CollectTableHeadCell>
                                        <CollectTableHeadCell>Status</CollectTableHeadCell>
                                        <CollectTableHeadCell align="right" />
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {invoices.map((inv) => (
                                        <TableRow
                                            key={inv._id}
                                            hover
                                            sx={{ cursor: "pointer" }}
                                            onClick={() => onRow(inv)}
                                        >
                                            <CollectTableBodyCell>
                                                <span style={{ fontWeight: 700 }}>
                                                    #{inv.docNumber || inv.qboId || inv.qboInvoiceId || "—"}
                                                </span>
                                            </CollectTableBodyCell>
                                            <CollectTableBodyCell>
                                                {getDisplayName(inv)}
                                            </CollectTableBodyCell>
                                            <CollectTableBodyCell>
                                                {formatDate(inv.txnDate || inv.issueDate)}
                                            </CollectTableBodyCell>
                                            <CollectTableBodyCell>
                                                {formatDate(inv.dueDate)}
                                            </CollectTableBodyCell>
                                            <CollectTableBodyCell align="right">
                                                {formatMoney(inv.totalAmount, inv.currency)}
                                            </CollectTableBodyCell>
                                            <CollectTableBodyCell align="right">
                                                <strong>
                                                    {formatMoney(inv.balance, inv.currency)}
                                                </strong>
                                            </CollectTableBodyCell>
                                            <CollectTableBodyCell>
                                                <CollectStatusChip
                                                    label={inv.status.replace("_", " ")}
                                                    statusKey={inv.status as any}
                                                    size="small"
                                                />
                                            </CollectTableBodyCell>
                                            <CollectTableBodyCell align="right">
                                                <Tooltip title="View invoice">
                                                    <IconButton
                                                        size="small"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            onRow(inv);
                                                        }}
                                                    >
                                                        <OpenInNew fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            </CollectTableBodyCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </CollectTableContainer>
                    )}

                    {/* Cursor pagination — backend returns nextCursor when there
                        are more results. We just refetch with cursor set; the
                        slice replaces the list, which is fine for now (no
                        infinite-scroll yet). */}
                    {nextCursor && (
                        <Box
                            sx={{
                                display: "flex",
                                justifyContent: "center",
                                mt: 2,
                            }}
                        >
                            <CollectGhostButton
                                onClick={() =>
                                    dispatch(
                                        fetchInvoices({
                                            ...queryPayload,
                                            cursor: nextCursor,
                                        }),
                                    )
                                }
                                disabled={isLoading}
                            >
                                {isLoading ? "Loading…" : "Load next page"}
                            </CollectGhostButton>
                        </Box>
                    )}
                </CollectSectionCard>
            </Box>
        </CollectPageWrapper>
    );
};

export default CollectInvoices;
